import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Copy, Download, Upload, RefreshCw, Check, X, AlertCircle, ChevronRight, ChevronDown, Search, Zap, Eye, EyeOff, ArrowUpDown, Hash, Type, Quote, Braces, Brackets, ToggleLeft, Minimize2, Maximize2, FileText, Settings, Filter, Code, Database, Globe, Calendar, MapPin, Link, Mail, Phone, Tag } from 'lucide-react';

interface XmlFormatterProps {}

type ViewMode = 'formatted' | 'tree';
type NodeType = 'element' | 'text' | 'attribute' | 'comment' | 'cdata' | 'declaration';

interface XmlNode {
  type: NodeType;
  name?: string;
  value?: string;
  attributes?: Record<string, string>;
  children?: XmlNode[];
  line?: number;
}

interface SearchResult {
  path: string;
  type: NodeType;
  name?: string;
  value?: string;
  attribute?: string;
}

const XmlFormatter: React.FC<XmlFormatterProps> = () => {
  const [input, setInput] = useState<string>('');
  const [parsedData, setParsedData] = useState<XmlNode | null>(null);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [error, setError] = useState<string>('');
  const [copied, setCopied] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState<number>(0);
  const lineNumberRef = useRef<number>(1);
  
  // View options
  const [viewMode, setViewMode] = useState<ViewMode>('tree');
  const [indentSize, setIndentSize] = useState<number>(2);
  const [sortAttributes, setSortAttributes] = useState<boolean>(false);
  const [showLineNumbers, setShowLineNumbers] = useState<boolean>(true);
  const [showNodeTypes, setShowNodeTypes] = useState<boolean>(true);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const [allExpanded, setAllExpanded] = useState<boolean>(true);
  const [highlightSyntax, setHighlightSyntax] = useState<boolean>(true);
  const [showAttributes, setShowAttributes] = useState<boolean>(true);
  const [showComments, setShowComments] = useState<boolean>(true);
  const [filterByType, setFilterByType] = useState<string>('all');
  const [highlightSearchResults, setHighlightSearchResults] = useState<boolean>(true);
  const [caseSensitiveSearch, setCaseSensitiveSearch] = useState<boolean>(false);
  const [searchInElements, setSearchInElements] = useState<boolean>(true);
  const [searchInAttributes, setSearchInAttributes] = useState<boolean>(true);
  const [searchInText, setSearchInText] = useState<boolean>(true);
  const [preserveWhitespace, setPreserveWhitespace] = useState<boolean>(false);
  const [selfClosingTags, setSelfClosingTags] = useState<boolean>(true);

  const parseXml = useCallback((xmlString: string): XmlNode | null => {
    if (!xmlString.trim()) return null;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlString, 'text/xml');
      
      // Check for parsing errors
      const parserError = doc.getElementsByTagName('parsererror')[0];
      if (parserError) {
        throw new Error(parserError.textContent || 'XML parsing error');
      }

      const convertDomToNode = (domNode: Node, lineNum = 1): XmlNode => {
        const node: XmlNode = { type: 'element', line: lineNum };

        switch (domNode.nodeType) {
          case Node.ELEMENT_NODE:
            const element = domNode as Element;
            node.type = 'element';
            node.name = element.tagName;
            
            // Parse attributes
            if (element.attributes.length > 0) {
              node.attributes = {};
              for (let i = 0; i < element.attributes.length; i++) {
                const attr = element.attributes[i];
                node.attributes[attr.name] = attr.value;
              }
            }
            
            // Parse children
            if (element.childNodes.length > 0) {
              node.children = [];
              Array.from(element.childNodes).forEach(child => {
                const childNode = convertDomToNode(child, lineNum);
                if (childNode.type !== 'text' || (childNode.value && childNode.value.trim())) {
                  node.children!.push(childNode);
                }
              });
            }
            break;

          case Node.TEXT_NODE:
            const textContent = domNode.textContent || '';
            if (preserveWhitespace || textContent.trim()) {
              node.type = 'text';
              node.value = textContent;
            }
            break;

          case Node.COMMENT_NODE:
            node.type = 'comment';
            node.value = domNode.textContent || '';
            break;

          case Node.CDATA_SECTION_NODE:
            node.type = 'cdata';
            node.value = domNode.textContent || '';
            break;

          default:
            node.type = 'text';
            node.value = domNode.textContent || '';
        }

        return node;
      };

      return convertDomToNode(doc.documentElement);
    } catch (err) {
      throw err;
    }
  }, [preserveWhitespace]);

  const validateXml = useCallback((xmlString: string) => {
    if (!xmlString.trim()) {
      setParsedData(null);
      setIsValid(null);
      setError('');
      setSearchResults([]);
      return;
    }

    try {
      const parsed = parseXml(xmlString);
      setParsedData(parsed);
      setIsValid(true);
      setError('');
      
      if (allExpanded && parsed) {
        const paths = getAllPaths(parsed);
        setExpandedPaths(new Set(paths));
      }

      // Update search results when data changes
      if (searchTerm && parsed) {
        performSearch(searchTerm, parsed);
      }
    } catch (err) {
      setIsValid(false);
      const errorMsg = err instanceof Error ? err.message : 'Invalid XML';
      setError(errorMsg);
      setParsedData(null);
      setSearchResults([]);
    }
  }, [allExpanded, searchTerm, parseXml]);

  const getAllPaths = (node: XmlNode, path: string = ''): string[] => {
    const paths: string[] = [];
    
    if (node.name) {
      const currentPath = path ? `${path}/${node.name}` : node.name;
      paths.push(currentPath);
      
      if (node.children) {
        node.children.forEach((child, index) => {
          if (child.type === 'element') {
            paths.push(...getAllPaths(child, currentPath));
          }
        });
      }
    }
    
    return paths;
  };

  const getNodeTypeColor = (type: NodeType): string => {
    const colors = {
      element: 'text-blue-700 bg-blue-100 border-blue-200',
      text: 'text-green-700 bg-green-100 border-green-200',
      attribute: 'text-purple-700 bg-purple-100 border-purple-200',
      comment: 'text-gray-600 bg-gray-100 border-gray-200',
      cdata: 'text-orange-700 bg-orange-100 border-orange-200',
      declaration: 'text-red-700 bg-red-100 border-red-200'
    };
    return colors[type] || colors.element;
  };

  const getNodeTypeIcon = (type: NodeType) => {
    const icons = {
      element: <Tag className="w-3 h-3" />,
      text: <Type className="w-3 h-3" />,
      attribute: <Hash className="w-3 h-3" />,
      comment: <Quote className="w-3 h-3" />,
      cdata: <Braces className="w-3 h-3" />,
      declaration: <Settings className="w-3 h-3" />
    };
    return icons[type] || <Tag className="w-3 h-3" />;
  };

  const performSearch = useCallback((term: string, data: XmlNode | null = parsedData) => {
    if (!term || !data) {
      setSearchResults([]);
      return;
    }

    const results: SearchResult[] = [];
    const searchLower = caseSensitiveSearch ? term : term.toLowerCase();

    const searchNode = (node: XmlNode, path: string = '') => {
      const currentPath = path ? `${path}/${node.name || 'text'}` : (node.name || 'text');

      // Search in element names
      if (searchInElements && node.name) {
        const nameToSearch = caseSensitiveSearch ? node.name : node.name.toLowerCase();
        if (nameToSearch.includes(searchLower)) {
          results.push({
            path: currentPath,
            type: node.type,
            name: node.name
          });
        }
      }

      // Search in attributes
      if (searchInAttributes && node.attributes) {
        Object.entries(node.attributes).forEach(([attrName, attrValue]) => {
          const nameToSearch = caseSensitiveSearch ? attrName : attrName.toLowerCase();
          const valueToSearch = caseSensitiveSearch ? attrValue : attrValue.toLowerCase();
          
          if (nameToSearch.includes(searchLower) || valueToSearch.includes(searchLower)) {
            results.push({
              path: currentPath,
              type: 'attribute',
              name: attrName,
              value: attrValue,
              attribute: attrName
            });
          }
        });
      }

      // Search in text content
      if (searchInText && node.value) {
        const valueToSearch = caseSensitiveSearch ? node.value : node.value.toLowerCase();
        if (valueToSearch.includes(searchLower)) {
          results.push({
            path: currentPath,
            type: node.type,
            value: node.value
          });
        }
      }

      // Continue recursive search
      if (node.children) {
        node.children.forEach(child => {
          searchNode(child, currentPath);
        });
      }
    };

    searchNode(data);
    setSearchResults(results);
    setCurrentSearchIndex(0);

    // Auto-expand paths containing search results
    if (results.length > 0) {
      const newExpanded = new Set(expandedPaths);
      results.forEach(result => {
        const pathParts = result.path.split('/').filter(Boolean);
        let currentPath = '';
        pathParts.forEach(part => {
          currentPath = currentPath ? `${currentPath}/${part}` : part;
          newExpanded.add(currentPath);
        });
      });
      setExpandedPaths(newExpanded);
    }
  }, [parsedData, caseSensitiveSearch, searchInElements, searchInAttributes, searchInText, expandedPaths]);

  useEffect(() => {
    performSearch(searchTerm);
  }, [searchTerm, performSearch]);

  const sortAttributesDeep = (node: XmlNode): XmlNode => {
    const newNode = { ...node };
    
    if (newNode.attributes) {
      const sortedAttrs: Record<string, string> = {};
      Object.keys(newNode.attributes)
        .sort()
        .forEach(key => {
          sortedAttrs[key] = newNode.attributes![key];
        });
      newNode.attributes = sortedAttrs;
    }
    
    if (newNode.children) {
      newNode.children = newNode.children.map(child => sortAttributesDeep(child));
    }
    
    return newNode;
  };

  const nodeToXml = (node: XmlNode, indent: number = 0, isRoot: boolean = false): string => {
    const indentStr = ' '.repeat(indent * indentSize);
    const processedNode = sortAttributes ? sortAttributesDeep(node) : node;
    
    switch (processedNode.type) {
      case 'element':
        if (!processedNode.name) return '';
        
        let xml = `${indentStr}<${processedNode.name}`;
        
        // Add attributes
        if (processedNode.attributes && showAttributes) {
          Object.entries(processedNode.attributes).forEach(([name, value]) => {
            xml += ` ${name}="${value}"`;
          });
        }
        
        if (!processedNode.children || processedNode.children.length === 0) {
          xml += selfClosingTags ? ' />' : `></${processedNode.name}>`;
        } else {
          xml += '>';
          
          const hasElementChildren = processedNode.children.some(child => child.type === 'element');
          
          if (hasElementChildren) {
            xml += '\n';
            processedNode.children.forEach(child => {
              if (child.type === 'comment' && !showComments) return;
              xml += nodeToXml(child, indent + 1);
            });
            xml += `${indentStr}</${processedNode.name}>`;
          } else {
            // Inline content
            processedNode.children.forEach(child => {
              if (child.type === 'text' && child.value) {
                xml += child.value;
              }
            });
            xml += `</${processedNode.name}>`;
          }
        }
        
        return xml + (isRoot ? '' : '\n');
        
      case 'text':
        return processedNode.value || '';
        
      case 'comment':
        return showComments ? `${indentStr}<!-- ${processedNode.value} -->\n` : '';
        
      case 'cdata':
        return `${indentStr}<![CDATA[${processedNode.value}]]>\n`;
        
      default:
        return '';
    }
  };

  const getFormattedXml = useMemo(() => {
    if (!parsedData) return '';
    return nodeToXml(parsedData, 0, true);
  }, [parsedData, sortAttributes, indentSize, showAttributes, showComments, selfClosingTags]);

  const getMinifiedXml = useMemo(() => {
    if (!parsedData) return '';
    const formatted = getFormattedXml;
    return formatted.replace(/>\s+</g, '><').replace(/\n\s*/g, '').trim();
  }, [getFormattedXml]);

  const togglePath = (path: string) => {
    const newExpanded = new Set(expandedPaths);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedPaths(newExpanded);
  };

  const expandAll = () => {
    if (parsedData) {
      const allPaths = getAllPaths(parsedData);
      setExpandedPaths(new Set(allPaths));
      setAllExpanded(true);
    }
  };

  const collapseAll = () => {
    setExpandedPaths(new Set());
    setAllExpanded(false);
  };

  const isSearchMatch = (path: string, name?: string, value?: string): boolean => {
    if (!searchTerm) return false;
    return searchResults.some(result => 
      result.path === path && 
      (result.name === name || result.value === value)
    );
  };

  const TreeNode: React.FC<{
    node: XmlNode;
    path: string;
    level: number;
    isLast?: boolean;
  }> = ({ node, path, level, isLast = false }) => {
    const isExpandable = node.children && node.children.length > 0;
    const isExpanded = expandedPaths.has(path);
    const shouldShow = filterByType === 'all' || filterByType === node.type;
    const isHighlighted = highlightSearchResults && isSearchMatch(path, node.name, node.value);

    if (!shouldShow) return null;

    const currentLineNumber = lineNumberRef.current++;

    const renderChildren = () => {
      if (!isExpandable || !isExpanded) return null;

      return node.children!.map((child, index) => {
        const childPath = child.name ? `${path}/${child.name}` : `${path}/text[${index}]`;
        return (
          <TreeNode
            key={`${childPath}-${index}`}
            node={child}
            path={childPath}
            level={level + 1}
            isLast={index === node.children!.length - 1}
          />
        );
      });
    };

    const renderNodeContent = () => {
      switch (node.type) {
        case 'element':
          return (
            <>
              <span className="text-blue-800 font-medium">&lt;{node.name}</span>
              {node.attributes && showAttributes && (
                <span className="ml-2">
                  {Object.entries(node.attributes).map(([name, value]) => (
                    <span key={name} className="ml-1">
                      <span className="text-purple-600">{name}</span>
                      <span className="text-gray-600">=</span>
                      <span className="text-green-600">"{value}"</span>
                    </span>
                  ))}
                </span>
              )}
              <span className="text-blue-800">&gt;</span>
              {!isExpandable && (
                <span className="text-blue-800">&lt;/{node.name}&gt;</span>
              )}
            </>
          );
          
        case 'text':
          return (
            <span className="text-green-700">
              {node.value && node.value.length > 100 
                ? `"${node.value.substring(0, 100)}..."` 
                : `"${node.value}"`}
            </span>
          );
          
        case 'comment':
          return (
            <span className="text-gray-600 italic">
              &lt;!-- {node.value} --&gt;
            </span>
          );
          
        case 'cdata':
          return (
            <span className="text-orange-700">
              &lt;![CDATA[{node.value}]]&gt;
            </span>
          );
          
        default:
          return <span className="text-gray-500">{node.value}</span>;
      }
    };

    return (
      <>
        <div className={`flex items-center py-1 group font-mono text-sm transition-colors ${
          isHighlighted ? 'bg-yellow-100 border-l-4 border-yellow-500' : 'hover:bg-blue-50'
        }`}>
          {/* Line Number */}
          {showLineNumbers && (
            <div className="w-12 text-right text-gray-400 text-xs mr-3 select-none flex-shrink-0">
              {currentLineNumber}
            </div>
          )}

          <div 
            className="flex items-center flex-1 min-w-0"
            style={{ paddingLeft: `${level * 20}px` }}
          >
            {/* Expand/Collapse Button */}
            {isExpandable ? (
              <button
                onClick={() => togglePath(path)}
                className="flex items-center justify-center w-4 h-4 mr-2 hover:bg-blue-200 rounded text-gray-600 flex-shrink-0"
              >
                {isExpanded ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
              </button>
            ) : (
              <div className="w-4 mr-2 flex-shrink-0" />
            )}

            {/* Node Type Badge */}
            {showNodeTypes && (
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium mr-2 border flex-shrink-0 ${getNodeTypeColor(node.type)}`}>
                {getNodeTypeIcon(node.type)}
                {node.type}
              </span>
            )}

            {/* Node Content */}
            <div className="flex-1 min-w-0">
              <span className="truncate block">{renderNodeContent()}</span>
            </div>

            {/* Path for debugging */}
            <span className="ml-2 text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 max-w-32 truncate">
              {path}
            </span>
          </div>
        </div>

        {/* Children */}
        {renderChildren()}

        {/* Closing tag */}
        {isExpandable && isExpanded && node.type === 'element' && (
          <div className="flex items-center py-1 font-mono text-sm">
            {showLineNumbers && (
              <div className="w-12 text-right text-gray-400 text-xs mr-3 select-none flex-shrink-0">
                {lineNumberRef.current++}
              </div>
            )}
            <div 
              className="text-blue-800"
              style={{ paddingLeft: `${(level + 1) * 20}px` }}
            >
              &lt;/{node.name}&gt;
            </div>
          </div>
        )}
      </>
    );
  };

  const renderTreeView = () => {
    lineNumberRef.current = 1;
    return (
      <div className="p-3 overflow-auto h-full">
        {parsedData && <TreeNode node={parsedData} path={parsedData.name || 'root'} level={0} />}
      </div>
    );
  };

  const renderFormattedView = () => {
    const content = getFormattedXml;
    const lines = content.split('\n');

    return (
      <div className="flex h-full border border-gray-300 rounded">
        {showLineNumbers && (
          <div className="bg-gray-50 border-r border-gray-300 px-3 py-2 text-right text-gray-500 font-mono text-xs min-w-12 select-none flex-shrink-0">
            {lines.map((_, index) => (
              <div key={index} className="leading-5 h-5">
                {index + 1}
              </div>
            ))}
          </div>
        )}
        <div className="flex-1 p-3 overflow-auto">
          <pre className="font-mono text-sm whitespace-pre-wrap">
            {lines.map((line, index) => (
              <div key={index} className="leading-5 h-5" dangerouslySetInnerHTML={{
                __html: highlightXmlLine(line)
              }} />
            ))}
          </pre>
        </div>
      </div>
    );
  };

  const highlightXmlLine = (line: string): string => {
    let highlighted = line
      .replace(/(&lt;)([^&\s]+)/g, '<span style="color: #0066cc; font-weight: 600;">$1$2</span>')
      .replace(/(&gt;)/g, '<span style="color: #0066cc; font-weight: 600;">$1</span>')
      .replace(/(\w+)=/g, '<span style="color: #800080;">$1</span>=')
      .replace(/"([^"]*)"/g, '<span style="color: #008000;">"$1"</span>')
      .replace(/(&lt;!--.*?--&gt;)/g, '<span style="color: #808080; font-style: italic;">$1</span>')
      .replace(/(&lt;!\[CDATA\[.*?\]\]&gt;)/g, '<span style="color: #ff6600;">$1</span>');

    // Highlight search terms
    if (searchTerm && highlightSearchResults) {
      const searchRegex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, caseSensitiveSearch ? 'g' : 'gi');
      highlighted = highlighted.replace(searchRegex, '<mark style="background-color: #ffeb3b; color: #000;">$1</mark>');
    }

    return highlighted;
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    validateXml(value);
  };

  const handleSortXml = () => {
    if (parsedData) {
      const sortedData = sortAttributesDeep(parsedData);
      const sortedString = nodeToXml(sortedData, 0, true);
      setInput(sortedString);
      validateXml(sortedString);
    }
  };

  const handleMinifyXml = () => {
    if (parsedData) {
      const minified = getMinifiedXml;
      setInput(minified);
      validateXml(minified);
    }
  };

  const handleFormatXml = () => {
    if (parsedData) {
      const formatted = getFormattedXml;
      setInput(formatted);
      validateXml(formatted);
    }
  };

  const navigateSearchResults = (direction: 'prev' | 'next') => {
    if (searchResults.length === 0) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentSearchIndex + 1) % searchResults.length;
    } else {
      newIndex = currentSearchIndex === 0 ? searchResults.length - 1 : currentSearchIndex - 1;
    }
    setCurrentSearchIndex(newIndex);

    // Expand path to show the current result
    const result = searchResults[newIndex];
    if (result) {
      const pathParts = result.path.split('/').filter(Boolean);
      const newExpanded = new Set(expandedPaths);
      let currentPath = '';
      pathParts.forEach(part => {
        currentPath = currentPath ? `${currentPath}/${part}` : part;
        newExpanded.add(currentPath);
      });
      setExpandedPaths(newExpanded);
    }
  };

  const handleCopy = async () => {
    const content = viewMode === 'formatted' ? getFormattedXml : input;
    if (content) {
      try {
        await navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleDownload = () => {
    const content = viewMode === 'formatted' ? getFormattedXml : input;
    if (content) {
      const blob = new Blob([content], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'formatted.xml';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInput(content);
        validateXml(content);
      };
      reader.readAsText(file);
    }
  };

  const handleClear = () => {
    setInput('');
    setParsedData(null);
    setIsValid(null);
    setError('');
    setExpandedPaths(new Set());
    setSearchTerm('');
    setSearchResults([]);
  };

  const loadSample = () => {
    const sample = `<?xml version="1.0" encoding="UTF-8"?>
<company>
  <info>
    <name>TechCorp Inc.</name>
    <founded>2020-01-15</founded>
    <website>https://techcorp.example.com</website>
    <email>contact@techcorp.com</email>
    <phone>+1-555-123-4567</phone>
  </info>
  
  <employees>
    <employee id="1" active="true">
      <name>John Doe</name>
      <email>john.doe@techcorp.com</email>
      <phone>+1-555-234-5678</phone>
      <department>Engineering</department>
      <salary currency="USD">75000.50</salary>
      <skills>
        <skill level="expert">JavaScript</skill>
        <skill level="advanced">React</skill>
        <skill level="intermediate">Node.js</skill>
        <skill level="beginner">Python</skill>
      </skills>
      <address>
        <street>123 Main St</street>
        <city>San Francisco</city>
        <state>CA</state>
        <zipCode>94105</zipCode>
        <coordinates>
          <latitude>37.7749</latitude>
          <longitude>-122.4194</longitude>
        </coordinates>
      </address>
      <projects>
        <project status="completed" priority="high">
          <name>Web Dashboard</name>
          <deadline>2024-03-15</deadline>
          <budget currency="USD">50000</budget>
        </project>
        <project status="in-progress" priority="medium">
          <name>Mobile App</name>
          <budget currency="USD">75000.25</budget>
          <!-- Deadline TBD -->
        </project>
      </projects>
    </employee>
    
    <employee id="2" active="false">
      <name>Jane Smith</name>
      <email>jane.smith@techcorp.com</email>
      <phone>+1-555-345-6789</phone>
      <department>Design</department>
      <salary currency="USD">68000</salary>
      <skills>
        <skill level="expert">Figma</skill>
        <skill level="expert">Photoshop</skill>
        <skill level="advanced">UI/UX</skill>
        <skill level="intermediate">Sketch</skill>
      </skills>
      <address>
        <street>456 Oak Ave</street>
        <city>Los Angeles</city>
        <state>CA</state>
        <zipCode>90210</zipCode>
      </address>
      <projects/>
    </employee>
  </employees>
  
  <departments>
    <department id="eng">
      <name>Engineering</name>
      <head>Alice Johnson</head>
      <budget currency="USD">500000</budget>
      <headcount>15</headcount>
    </department>
    <department id="design">
      <name>Design</name>
      <head>Bob Wilson</head>
      <budget currency="USD">200000</budget>
      <headcount>8</headcount>
    </department>
    <department id="marketing">
      <name>Marketing</name>
      <head>Carol Brown</head>
      <budget currency="USD">150000</budget>
      <headcount>5</headcount>
    </department>
  </departments>
  
  <metadata>
    <version>2.1.0</version>
    <lastUpdated>2024-05-22T14:30:00Z</lastUpdated>
    <features>
      <feature enabled="true">analytics</feature>
      <feature enabled="true">reporting</feature>
      <feature enabled="false">automation</feature>
    </features>
    <settings>
      <theme>dark</theme>
      <notifications enabled="true"/>
      <autoSave enabled="false"/>
      <maxFileSize unit="bytes">10485760</maxFileSize>
    </settings>
    <!-- <![CDATA[This is a CDATA section with special characters: <>&"']] -->
  </metadata>
</company>`;
    
    setInput(sample);
    validateXml(sample);
  };

  // Helper functions
  const countNodes = (node: XmlNode, count = 0): number => {
    count++;
    if (node.children) {
      node.children.forEach(child => {
        count = countNodes(child, count);
      });
    }
    return count;
  };

  const countAttributes = (node: XmlNode, count = 0): number => {
    if (node.attributes) {
      count += Object.keys(node.attributes).length;
    }
    if (node.children) {
      node.children.forEach(child => {
        count = countAttributes(child, count);
      });
    }
    return count;
  };

  const getMaxDepth = (node: XmlNode, depth = 0): number => {
    if (!node.children || node.children.length === 0) {
      return depth;
    }
    return Math.max(...node.children.map(child => getMaxDepth(child, depth + 1)));
  };

  const getNodeTypeDistribution = (node: XmlNode): Record<string, number> => {
    const distribution: Record<string, number> = {};
    
    const countTypes = (n: XmlNode) => {
      distribution[n.type] = (distribution[n.type] || 0) + 1;
      
      if (n.children) {
        n.children.forEach(child => countTypes(child));
      }
    };
    
    countTypes(node);
    return distribution;
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto p-4">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Advanced XML Formatter Pro</h1>
          <p className="text-gray-600">Professional XML formatter with advanced search, validation, and tree view</p>
        </div>

        {/* Main Toolbar */}
        <div className="bg-gray-50 border border-gray-300 rounded p-4 mb-4">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <button
              onClick={() => validateXml(input)}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium transition-colors"
            >
              <Check className="w-4 h-4 inline mr-1" />
              Validate XML
            </button>

            <button
              onClick={handleFormatXml}
              disabled={!parsedData}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors"
            >
              <Maximize2 className="w-4 h-4 inline mr-1" />
              Format
            </button>

            <button
              onClick={handleMinifyXml}
              disabled={!parsedData}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 font-medium transition-colors"
            >
              <Minimize2 className="w-4 h-4 inline mr-1" />
              Minify
            </button>

            <button
              onClick={handleSortXml}
              disabled={!parsedData}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 font-medium transition-colors"
            >
              <ArrowUpDown className="w-4 h-4 inline mr-1" />
              Sort Attributes
            </button>
            
            <button
              onClick={handleCopy}
              disabled={!parsedData}
              className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 font-medium transition-colors"
            >
              <Copy className="w-4 h-4 inline mr-1" />
              {copied ? 'Copied!' : 'Copy'}
            </button>

            <button
              onClick={handleDownload}
              disabled={!parsedData}
              className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 font-medium transition-colors"
            >
              <Download className="w-4 h-4 inline mr-1" />
              Download
            </button>

            <label className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 cursor-pointer font-medium transition-colors">
              <Upload className="w-4 h-4 inline mr-1" />
              Upload File
              <input
                type="file"
                accept=".xml,.xsd,.xsl,.xslt"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>

            <button
              onClick={handleClear}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium transition-colors"
            >
              <RefreshCw className="w-4 h-4 inline mr-1" />
              Clear
            </button>

            <button
              onClick={loadSample}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 font-medium transition-colors"
            >
              <Zap className="w-4 h-4 inline mr-1" />
              Sample
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">View:</label>
              <select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value as ViewMode)}
                className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="tree">Tree View</option>
                <option value="formatted">Formatted View</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Indent:</label>
              <select
                value={indentSize}
                onChange={(e) => setIndentSize(Number(e.target.value))}
                className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value={2}>2 spaces</option>
                <option value={4}>4 spaces</option>
                <option value={8}>8 spaces</option>
                <option value={0}>Compact</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Filter:</label>
              <select
                value={filterByType}
                onChange={(e) => setFilterByType(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="element">Elements</option>
                <option value="text">Text</option>
                <option value="attribute">Attributes</option>
                <option value="comment">Comments</option>
                <option value="cdata">CDATA</option>
              </select>
            </div>

            {viewMode === 'tree' && parsedData && (
              <div className="flex gap-2">
                <button
                  onClick={expandAll}
                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                >
                  <Eye className="w-3 h-3 inline mr-1" />
                  Expand All
                </button>
                <button
                  onClick={collapseAll}
                  className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 transition-colors"
                >
                  <EyeOff className="w-3 h-3 inline mr-1" />
                  Collapse All
                </button>
              </div>
            )}
          </div>

          {/* Advanced Options */}
          <div className="border-t border-gray-300 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Settings className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Advanced Options</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showLineNumbers}
                  onChange={(e) => setShowLineNumbers(e.target.checked)}
                  className="w-4 h-4 text-blue-600"
                />
                Line Numbers
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showNodeTypes}
                  onChange={(e) => setShowNodeTypes(e.target.checked)}
                  className="w-4 h-4 text-blue-600"
                />
                Node Types
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showAttributes}
                  onChange={(e) => setShowAttributes(e.target.checked)}
                  className="w-4 h-4 text-blue-600"
                />
                Show Attributes
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showComments}
                  onChange={(e) => setShowComments(e.target.checked)}
                  className="w-4 h-4 text-blue-600"
                />
                Show Comments
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selfClosingTags}
                  onChange={(e) => setSelfClosingTags(e.target.checked)}
                  className="w-4 h-4 text-blue-600"
                />
                Self-closing Tags
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={highlightSearchResults}
                  onChange={(e) => setHighlightSearchResults(e.target.checked)}
                  className="w-4 h-4 text-blue-600"
                />
                Highlight Results
              </label>
            </div>
          </div>
        </div>

        {/* Search Panel */}
        <div className="bg-white border border-gray-300 rounded p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Advanced Search & Filter</span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search elements, attributes, and text..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                {searchResults.length > 0 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigateSearchResults('prev')}
                      className="px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                      title="Previous result"
                    >
                      ←
                    </button>
                    <span className="text-sm text-gray-600 min-w-20 text-center">
                      {currentSearchIndex + 1} of {searchResults.length}
                    </span>
                    <button
                      onClick={() => navigateSearchResults('next')}
                      className="px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                      title="Next result"
                    >
                      →
                    </button>
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap gap-3 mt-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={caseSensitiveSearch}
                    onChange={(e) => setCaseSensitiveSearch(e.target.checked)}
                    className="w-4 h-4 text-blue-600"
                  />
                  Case Sensitive
                </label>
                
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={searchInElements}
                    onChange={(e) => setSearchInElements(e.target.checked)}
                    className="w-4 h-4 text-blue-600"
                  />
                  Search in Elements
                </label>
                
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={searchInAttributes}
                    onChange={(e) => setSearchInAttributes(e.target.checked)}
                    className="w-4 h-4 text-blue-600"
                  />
                  Search in Attributes
                </label>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={searchInText}
                    onChange={(e) => setSearchInText(e.target.checked)}
                    className="w-4 h-4 text-blue-600"
                  />
                  Search in Text
                </label>
              </div>
            </div>
            
            <div>
              <div className="text-sm text-gray-600 space-y-1">
                {searchResults.length > 0 ? (
                  <>
                    <div className="font-medium">Search Results: {searchResults.length}</div>
                    <div className="max-h-20 overflow-y-auto text-xs">
                      {searchResults.slice(0, 5).map((result, index) => (
                        <div key={index} className="truncate">
                          {result.path}: {result.name || result.value?.substring(0, 30)}...
                        </div>
                      ))}
                      {searchResults.length > 5 && (
                        <div className="text-gray-500">... and {searchResults.length - 5} more</div>
                      )}
                    </div>
                  </>
                ) : searchTerm ? (
                  <div>No results found</div>
                ) : (
                  <div>Enter search term to find matches</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* Input Panel */}
          <div>
            <div className="bg-gray-100 px-4 py-2 border border-gray-300 rounded-t font-medium text-gray-700 flex items-center justify-between">
              <span>XML Input</span>
              {isValid !== null && (
                <div className="flex items-center gap-2">
                  {isValid ? (
                    <>
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-green-600 text-sm">Valid XML</span>
                    </>
                  ) : (
                    <>
                      <X className="w-4 h-4 text-red-600" />
                      <span className="text-red-600 text-sm">Invalid XML</span>
                    </>
                  )}
                </div>
              )}
            </div>
            <textarea
              value={input}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder="Paste XML here..."
              className="w-full p-3 border border-gray-300 border-t-0 rounded-b font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ height: '600px' }}
            />
            {error && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-medium">Error:</span>
                </div>
                <p className="text-red-600 text-sm mt-1 font-mono">{error}</p>
              </div>
            )}
          </div>

          {/* Output Panel */}
          <div>
            <div className="bg-gray-100 px-4 py-2 border border-gray-300 rounded-t font-medium text-gray-700 flex items-center justify-between">
              <span>{viewMode === 'tree' ? 'Tree View' : 'Formatted XML'}</span>
              {parsedData && (
                <span className="text-sm text-gray-500">
                  {viewMode === 'tree' ? 'Interactive Explorer' : `${getFormattedXml.split('\n').length} lines`}
                </span>
              )}
            </div>
            <div className="border border-gray-300 border-t-0 rounded-b bg-white overflow-hidden" style={{ height: '600px' }}>
              {!parsedData ? (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <Code className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>Output will appear here...</p>
                    <p className="text-xs text-gray-400 mt-1">Paste valid XML to get started</p>
                  </div>
                </div>
              ) : viewMode === 'tree' ? (
                renderTreeView()
              ) : (
                <div className="h-full overflow-auto">
                  {renderFormattedView()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Analytics Dashboard */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Statistics Panel */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              XML Analytics
            </h3>
            {parsedData ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Size:</span>
                  <span className="font-medium">{new Blob([getFormattedXml]).size} bytes</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Characters:</span>
                  <span className="font-medium">{getFormattedXml.length.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Lines (formatted):</span>
                  <span className="font-medium">{getFormattedXml.split('\n').length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Nodes:</span>
                  <span className="font-medium">{countNodes(parsedData)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Attributes:</span>
                  <span className="font-medium">{countAttributes(parsedData)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Max Depth:</span>
                  <span className="font-medium">{getMaxDepth(parsedData)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Search Results:</span>
                  <span className="font-medium">{searchResults.length}</span>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Load XML to see analytics</p>
            )}
          </div>

          {/* Node Type Distribution */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Tag className="w-5 h-5 text-purple-600" />
              Node Types
            </h3>
            {parsedData ? (
              <div className="space-y-2">
                {Object.entries(getNodeTypeDistribution(parsedData)).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${getNodeTypeColor(type as NodeType)}`}>
                        {getNodeTypeIcon(type as NodeType)}
                        {type}
                      </span>
                    </div>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Load XML to see node distribution</p>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-600" />
              Quick Actions
            </h3>
            <div className="space-y-3">
              <button
                onClick={() => setSearchTerm('email')}
                className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded transition-colors"
              >
                <Mail className="w-4 h-4 inline mr-2" />
                Find email elements
              </button>
              <button
                onClick={() => setSearchTerm('http')}
                className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded transition-colors"
              >
                <Link className="w-4 h-4 inline mr-2" />
                Find URL elements
              </button>
              <button
                onClick={() => setFilterByType('comment')}
                className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded transition-colors"
              >
                <Quote className="w-4 h-4 inline mr-2" />
                Show only comments
              </button>
              <button
                onClick={() => setFilterByType('attribute')}
                className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded transition-colors"
              >
                <Hash className="w-4 h-4 inline mr-2" />
                Show only attributes
              </button>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterByType('all');
                }}
                className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded transition-colors"
              >
                <Eye className="w-4 h-4 inline mr-2" />
                Reset all filters
              </button>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 text-center">🚀 Professional XML Features</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Real-time XML Validation
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Interactive Tree View with Line Numbers
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Advanced Search & Navigation
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Node Type Detection & Filtering
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Attribute Management & Sorting
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              XML Analytics Dashboard
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Format & Minify Functions
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Comment & CDATA Support
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Self-closing Tag Options
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Search Result Highlighting
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              File Upload/Download (.xml, .xsd, .xsl)
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Quick Action Shortcuts
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default XmlFormatter;